import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { ga, setUserId, clearUserId } from "../analytics/ga";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "fij_token";
const USER_KEY  = "fij_user";

/* ---- axios interceptor: attach Bearer, redirect on 401 ---- */
axios.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t && !cfg.headers?.Authorization) {
    cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${t}` };
  }
  return cfg;
});
axios.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && (err.config?.url || "").startsWith(API)) {
      const path = window.location.pathname;
      const isAuthCall = err.config.url.includes("/api/auth/");
      const hadToken = Boolean(localStorage.getItem(TOKEN_KEY));
      // Only bounce the user out on 401s from PROTECTED endpoints when they
      // HAD a token (i.e. stale/expired session) — never redirect guests.
      if (hadToken && !isAuthCall && !path.startsWith("/login") && !path.startsWith("/signup")) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        window.location.assign("/login");
      }
    }
    return Promise.reject(err);
  }
);

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  // undefined = still checking, null = logged out, object = logged in
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setUser(null); return; }
    axios.get(`${API}/auth/me`)
      .then((r) => {
        setUser(r.data);
        localStorage.setItem(USER_KEY, JSON.stringify(r.data));
        setUserId(r.data?.id);
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setUser(null);
      });
  }, []);

  const persist = useCallback((token, u) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(u));
    setUser(u);
  }, []);

  const signIn = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    persist(data.token, data.user);
    setUserId(data.user?.id);
    ga.login("email");
    return data.user;
  }, [persist]);

  const registerFinalize = useCallback(async (email, accessKey) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, access_key: accessKey });
    persist(data.token, data.user);
    setUserId(data.user?.id);
    ga.signUp("email");
    return data.user;
  }, [persist]);

  const generateAccessKey = useCallback(async (fullName, email, password) => {
    const { data } = await axios.post(`${API}/auth/generate-key`, {
      full_name: fullName, email, password,
    });
    return data.access_key;
  }, []);

  const updateProfile = useCallback(async (patch) => {
    const { data } = await axios.patch(`${API}/auth/me`, patch);
    setUser(data);
    localStorage.setItem(USER_KEY, JSON.stringify(data));
    return data;
  }, []);

  const signOut = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`); } catch { /* ignore */ }
    clearUserId();
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    window.location.assign("/login");
  }, []);

  return (
    <AuthCtx.Provider value={{ user, signIn, signOut, generateAccessKey, registerFinalize, updateProfile }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

/** helper for FastAPI error payloads (detail can be string, array, or object). */
export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (d == null) return err?.message || "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (d && typeof d.msg === "string") return d.msg;
  return String(d);
}
